import { useState, useEffect, useCallback, useRef } from 'react';

const useIdleTimeout = (
    idleTimeoutMinutes: number,
    warningDurationSeconds: number = 120
): {
    showTimeoutDialog: boolean;
    resetIdleTimer: () => void;
    immediateLogout: boolean;
} => {
    const [showTimeoutDialog, setShowTimeoutDialog] = useState<boolean>(false);
    const [immediateLogout, setImmediateLogout] = useState<boolean>(false);
    const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isDialogShowingRef = useRef<boolean>(false);
    const lastActivityTimeRef = useRef<number>(Date.now());

    const resetIdleTimer = useCallback((): void => {
        if (idleTimerRef.current) {
            clearTimeout(idleTimerRef.current);
        }
        setShowTimeoutDialog(false);
        setImmediateLogout(false);
        isDialogShowingRef.current = false;
        lastActivityTimeRef.current = Date.now();
        idleTimerRef.current = setTimeout(() => {
            setShowTimeoutDialog(true);
            isDialogShowingRef.current = true;
        }, idleTimeoutMinutes * 60 * 1000);
    }, [idleTimeoutMinutes]);

    useEffect(() => {
        const activityEvents: string[] = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll', 'click'];

        const handleActivity = (): void => {
            if (!isDialogShowingRef.current) {
                resetIdleTimer();
            }
        };

        const handleVisibilityChange = (): void => {
            if (document.visibilityState === 'visible') {
                const elapsedMs = Date.now() - lastActivityTimeRef.current;
                const idleTimeoutMs = idleTimeoutMinutes * 60 * 1000;
                const totalTimeoutMs = idleTimeoutMs + warningDurationSeconds * 1000;

                if (elapsedMs >= totalTimeoutMs) {
                    // Both idle timeout and warning countdown have expired — log out immediately
                    if (idleTimerRef.current) {
                        clearTimeout(idleTimerRef.current);
                    }
                    isDialogShowingRef.current = false;
                    setShowTimeoutDialog(false);
                    setImmediateLogout(true);
                } else if (elapsedMs >= idleTimeoutMs) {
                    // Idle timeout expired but still within warning window — show dialog
                    if (idleTimerRef.current) {
                        clearTimeout(idleTimerRef.current);
                    }
                    setShowTimeoutDialog(true);
                    isDialogShowingRef.current = true;
                } else if (!isDialogShowingRef.current) {
                    // Still within idle timeout — restart timer with remaining time
                    if (idleTimerRef.current) {
                        clearTimeout(idleTimerRef.current);
                    }
                    const remainingMs = idleTimeoutMs - elapsedMs;
                    idleTimerRef.current = setTimeout(() => {
                        setShowTimeoutDialog(true);
                        isDialogShowingRef.current = true;
                    }, remainingMs);
                }
            }
        };

        activityEvents.forEach((event) => window.addEventListener(event, handleActivity));
        document.addEventListener('visibilitychange', handleVisibilityChange);
        resetIdleTimer();

        return (): void => {
            activityEvents.forEach((event) => window.removeEventListener(event, handleActivity));
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (idleTimerRef.current) {
                clearTimeout(idleTimerRef.current);
            }
        };
    }, [resetIdleTimer, idleTimeoutMinutes, warningDurationSeconds]);

    return { showTimeoutDialog, resetIdleTimer, immediateLogout };
};

export default useIdleTimeout;
